import { useId } from 'react'
import type { RepositoryType } from '../../utils/api-client/api-schemas.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { Checkbox } from '../input/checkbox.tsx'
import { SCMIcon } from '../third-party-branding/scm-icon.tsx'
import { ProjectChip } from './project-chip.tsx'
import { eyebrow } from './project-dialog.css.ts'
import * as styles from './repository-pick-list.css.ts'

export type PickableRepository = {
  id: string
  fullName: string
  type: RepositoryType
  /** The project the repository is in today, when it has one. */
  currentProjectName?: string
}

export type RepositoryPickListProps = {
  /** The heading over the list: what these candidates are. */
  label: string
  candidates: PickableRepository[]
  /**
   * How many repositories the list stands for, as the API reported it: the rows are one page of the
   * search's answer, so the header counts the answer rather than the page. Left out while it is not
   * known, and the header then shows no count rather than the page's.
   */
  total?: number
  pickedIds: ReadonlySet<string>
  onToggle: (repository: PickableRepository) => unknown
  /**
   * When set, every row that is not already picked goes dead and this says why — the selection cap's
   * "Up to 2 more: a project holds at most 100.". Checked rows stay live so the reader can trade one pick for another.
   */
  unpickedDisabledReason?: string
}

/** A striped multi-select of repositories, each with the project it would be taken from. */
export const RepositoryPickList: React.FC<RepositoryPickListProps> = ({
  label,
  candidates,
  total,
  pickedIds,
  onToggle,
  unpickedDisabledReason,
}) => {
  // Generated rather than derived from the label: two lists can share a label, and a label with no
  // word characters produces no id at all.
  const reasonId = useId()
  const rowIdPrefix = useId()
  // One boolean for both the disabling and the note, so a blank reason cannot disable rows while
  // describing them with nothing.
  const hasCapReason = unpickedDisabledReason !== undefined && unpickedDisabledReason !== ''
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <p className={eyebrow}>{label}</p>
        {total !== undefined && (
          <span className={styles.count}>
            {total} {pluralize(total, 'repository', 'repositories')}
          </span>
        )}
      </div>
      {hasCapReason && (
        <p className={styles.capNote} id={reasonId} role="status">
          {unpickedDisabledReason}
        </p>
      )}
      <div className={styles.picker} role="group" aria-label={label}>
        {candidates.map(repository => {
          const checkboxId = `${rowIdPrefix}-${repository.id}`
          const isPicked = pickedIds.has(repository.id)
          const isDisabled = hasCapReason && !isPicked
          return (
            <div key={repository.id} className={styles.row}>
              <Checkbox
                id={checkboxId}
                checked={isPicked}
                state={isDisabled ? 'disabled' : 'default'}
                onCheckedChange={() => onToggle(repository)}
                ariaLabel={`Select ${repository.fullName}`}
                aria-describedby={isDisabled ? reasonId : undefined}
              />
              <label className={isDisabled ? styles.nameDisabled : styles.name} htmlFor={checkboxId}>
                <SCMIcon type={repository.type} size="medium" />
                <span className={styles.nameText}>{repository.fullName}</span>
              </label>
              <span className={styles.spacer} />
              {repository.currentProjectName && <ProjectChip name={repository.currentProjectName} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}
